import React from 'react';
import { Button, type ButtonProps } from '@mui/material';

import { useRequestPayment } from '../../hooks/useRequestPayment';

export interface RequestPaymentButtonProps extends Omit<ButtonProps, 'onClick'> {
  // Payment request fields in camelCase
  amount?: string;
  patientId?: string;
  patientName?: string; // For display in modal
  reason?: string;
  dueDate?: string;
  appointmentId?: string;
  encounterId?: string;
  actionId?: string;
  // Source of the payment request (e.g., 'patient', 'schedule', 'action')
  source?: string;
  // Patient contact info (optional)
  patientTelephone?: string;
  // Whether patient has a payment method on file (changes copy to "Charge" instead of "Request")
  hasPaymentMethod?: boolean;
  // Callback after successful payment request
  onSuccess?: () => void;
  // Override default label
  label?: string;
  // Children to render inside button
  children?: React.ReactNode;
}

/**
 * RequestPaymentButton - A smart button for requesting payments
 *
 * This component provides a UI wrapper around the headless useRequestPayment hook.
 * All business logic has been extracted to the hook for reusability.
 *
 * Behavior:
 * 1. If no Stripe account connected: Opens Stripe Connect modal
 * 2. If Stripe connected + all required fields: Sends payment request directly
 * 3. If Stripe connected + missing fields: Opens payment request form modal
 */
export const RequestPaymentButton: React.FC<RequestPaymentButtonProps> = ({
  amount,
  patientId,
  patientName,
  reason,
  dueDate,
  appointmentId,
  encounterId,
  actionId,
  source,
  patientTelephone,
  hasPaymentMethod,
  onSuccess,
  label,
  children,
  ...buttonProps
}) => {
  // Use the headless hook for all business logic
  const { isLoading, triggerPaymentRequest } = useRequestPayment({
    amount,
    patientId,
    patientName,
    reason,
    dueDate,
    appointmentId,
    encounterId,
    actionId,
    source,
    patientTelephone,
    hasPaymentMethod,
    onSuccess,
  });

  // Determine default label based on whether patient has payment method on file
  const defaultLabel = hasPaymentMethod ? 'Charge Payment' : 'Request Payment';
  const loadingLabel = hasPaymentMethod ? 'Charging...' : 'Sending...';

  return (
    <Button onClick={triggerPaymentRequest} disabled={isLoading} {...buttonProps}>
      {children || (isLoading ? loadingLabel : (label ?? defaultLabel))}
    </Button>
  );
};
